import { useCallback, useEffect, useRef } from "react";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import onaWaitingAnimation from "@/assets/animations/ona-waiting.json";
import { cn } from "@/components/podkit/lib/cn";

type OnaWaitingProps = {
    /**
     * Time in milliseconds between animation cycles (default: 75000ms / 75 seconds)
     *
     * Rationale: This is for empty state where Ona is patiently waiting for user input.
     * The long interval creates a patient, non-intrusive presence.
     *
     * Timing research:
     * - < 30s: Feels repetitive and demanding
     * - 30-45s: Noticeable but acceptable
     * - 60-90s: Patient, occasional reminder of presence
     * - 75s: Sweet spot - roughly once per minute
     *
     * Note: Intentionally longer than OnaTips (8s) because:
     * - OnaTips: Active content rotation (user is engaged)
     * - OnaWaiting: Passive presence (user might be reading/thinking)
     */
    cycleInterval?: number;
    /** Size of the animation (default: md / 32px) */
    size?: "sm" | "md" | "lg";
    /** Additional CSS classes */
    className?: string;
};

const sizeClasses = {
    sm: "size-6", // 24px
    md: "size-8", // 32px
    lg: "size-12", // 48px
} as const;

/**
 * OnaWaiting component displays a Lottie animation of the Ona icon in a waiting state.
 * The animation starts paused on the first frame, plays after a delay, then pauses
 * on the last frame. This cycle repeats until the component unmounts.
 *
 * Designed for empty state / waiting scenarios where Ona is patiently waiting
 * for the user to start a session. The long interval (75s default) creates
 * a patient, non-intrusive presence.
 *
 * Location: components/ona/ is intentional for grouping Ona-specific UI components.
 * Future components like OnaThinking, OnaProcessing would also live here.
 */
export const OnaWaiting = ({ cycleInterval = 75000, size = "md", className }: OnaWaitingProps) => {
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Start the next animation cycle
    const startNextCycle = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            // Play animation from the beginning
            lottieRef.current?.goToAndPlay(0, true);
        }, cycleInterval);
    }, [cycleInterval]);

    // Start cycling on mount
    useEffect(() => {
        // Pause on first frame initially
        lottieRef.current?.goToAndStop(0, true);

        // Start the first cycle
        startNextCycle();

        // Cleanup on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [startNextCycle]);

    // Handle animation complete - start next cycle
    const handleAnimationComplete = useCallback(() => {
        // Animation will naturally pause on last frame since loop={false}
        // Start the next cycle
        startNextCycle();
    }, [startNextCycle]);

    return (
        <div
            className={cn("relative", sizeClasses[size], className)}
            style={{
                // Theme-aware color filter defined in index.css
                // Light mode: brightness(0.13) saturate(0) - darkens to RGB(26,26,26)
                // Dark mode: brightness(1.288) saturate(0) - brightens to RGB(255,255,255) white
                filter: "var(--lottie-color-filter)",
            }}
            role="img"
            aria-label="Ona waiting"
        >
            <Lottie
                lottieRef={lottieRef}
                animationData={onaWaitingAnimation}
                loop={false}
                autoplay={false}
                onComplete={handleAnimationComplete}
                className="size-full"
                rendererSettings={{
                    preserveAspectRatio: "xMidYMid meet",
                }}
            />
        </div>
    );
};
